import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { onboardingApi } from 'entities/onboarding/api/onboardingApi';
import { onboardingKeys } from 'entities/onboarding/api/queryKeys';
import type { StepStatus } from 'entities/onboarding/types';

type Options = { enabled?: boolean };

export function useChecklist({ enabled = true }: Options = {}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: onboardingKeys.checklist(),
    queryFn: () => onboardingApi.getChecklist(),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    // A 404 before the wizard is completed is expected (callers also gate `enabled` on
    // wizard_complete), and the client's 4xx errors carry no status to branch on — so retry only
    // transient server (5xx) errors, which do.
    retry: (failureCount, error) =>
      failureCount < 2 &&
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      typeof error.status === 'number' &&
      error.status >= 500,
  });

  // Awaited so a mutation resolves only once the checklist has refetched, and callers can act on the
  // step that is current *after* the change rather than the one that was.
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: onboardingKeys.checklist() }),
      queryClient.invalidateQueries({ queryKey: onboardingKeys.state() }),
    ]);

  const updateStep = useMutation({
    mutationFn: ({ stepId, status }: { stepId: string; status: StepStatus }) =>
      onboardingApi.updateStep(stepId, status),
    onSuccess: invalidate,
  });

  const syncChecklist = useMutation({
    mutationFn: () => onboardingApi.syncChecklist(),
    onSuccess: invalidate,
  });

  // The mutations are returned whole rather than as bare `mutateAsync`, so callers read in-flight
  // state off them instead of tracking a parallel copy.
  return { query, updateStep, syncChecklist };
}
